import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SITE, INTEREST_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    organisation: "",
    interest: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please complete name, email and message.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                organisation: form.organisation.trim() || null,
                interest: form.interest || null,
                message: form.message.trim(),
            });
            toast.success("Message received. We'll be in touch.");
            setForm(initial);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Could not send your message. Please try again or email us directly.";
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 lg:py-40"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-12 gap-6 md:gap-16">
                    <div className="col-span-12 md:col-span-5 lg:col-span-5">
                        <span className="number-mark text-ink2">§ 09</span>
                        <h2
                            data-testid="contact-heading"
                            className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink"
                        >
                            Interested
                            <br />
                            in working
                            <br />
                            <em className="italic font-light text-eucalyptus">
                                together?
                            </em>
                        </h2>

                        <p className="mt-8 text-ink2 text-lg leading-relaxed max-w-md">
                            Caring for Country is available for strategy,
                            project design, partnership development, policy
                            advice, investment planning and Indigenous-led
                            restoration initiatives.
                        </p>

                        <div className="mt-12 space-y-6">
                            <div>
                                <span className="eyebrow text-ink2">
                                    Email
                                </span>
                                <a
                                    data-testid="contact-mailto"
                                    href={`mailto:${SITE.email}`}
                                    className="mt-2 block font-serif text-2xl md:text-3xl text-ink editorial-link"
                                >
                                    {SITE.email}
                                </a>
                            </div>
                            <div>
                                <span className="eyebrow text-ink2">Web</span>
                                <span className="mt-2 block font-serif text-xl md:text-2xl text-ink2">
                                    {SITE.domain}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-7 lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="border-t border-rule pt-10 space-y-8"
                            noValidate
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <label className="block">
                                    <span className="eyebrow text-ink2">
                                        Name
                                    </span>
                                    <input
                                        data-testid="contact-name"
                                        name="name"
                                        type="text"
                                        value={form.name}
                                        onChange={onChange}
                                        autoComplete="name"
                                        className="input-line"
                                        placeholder="Your full name"
                                        required
                                    />
                                </label>
                                <label className="block">
                                    <span className="eyebrow text-ink2">
                                        Email
                                    </span>
                                    <input
                                        data-testid="contact-email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        autoComplete="email"
                                        className="input-line"
                                        placeholder="you@organisation.org"
                                        required
                                    />
                                </label>
                                <label className="block">
                                    <span className="eyebrow text-ink2">
                                        Organisation
                                    </span>
                                    <input
                                        data-testid="contact-organisation"
                                        name="organisation"
                                        type="text"
                                        value={form.organisation}
                                        onChange={onChange}
                                        className="input-line"
                                        placeholder="Optional"
                                    />
                                </label>
                                <label className="block">
                                    <span className="eyebrow text-ink2">
                                        Area of interest
                                    </span>
                                    <select
                                        data-testid="contact-interest"
                                        name="interest"
                                        value={form.interest}
                                        onChange={onChange}
                                        className="input-line appearance-none cursor-pointer pr-6"
                                        style={{
                                            backgroundImage:
                                                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='none' stroke='%231E1D1B' stroke-width='1.2' d='M1 1l4 4 4-4'/></svg>\")",
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition:
                                                "right 0 center",
                                        }}
                                    >
                                        <option value="">Select one…</option>
                                        {INTEREST_OPTIONS.map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <label className="block">
                                <span className="eyebrow text-ink2">
                                    Message
                                </span>
                                <textarea
                                    data-testid="contact-message"
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    className="input-line resize-none"
                                    placeholder="Tell us about the work, the Country, the partnership you have in mind."
                                    required
                                />
                            </label>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                                <p className="text-xs text-ink2 max-w-sm leading-relaxed">
                                    By submitting, you agree to be contacted
                                    about your enquiry. We respect your data
                                    and will never share your details.
                                </p>
                                <button
                                    data-testid="contact-submit"
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center px-7 py-4 bg-ink text-bone text-[11px] tracking-[0.22em] uppercase hover:bg-eucalyptus transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Sending…" : "Send message"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
